// src/prototype/data/adapters/stage300ExecutionPlan.ts
// ============================================================
// EPM (Execution Plan Management) SNAPSHOT — client 300
//
// Snapshot/seed layer. Baked from `dva-stage.dva_dataset.epm_*` for client_id = "300",
// plan run `epm-300-sep-v105` (plan_type `sep`, status success, generated 2026-07-20T06:21:16Z,
// portfolio [1553, 1554, 1555, 1559, 1683]). Read-only: the frontend never writes dva-stage.
//
// WHY A SNAPSHOT AND NOT A FETCH: the prototype has no authenticated path to BigQuery. Every
// stage-derived surface in src/prototype/data/adapters/ is a baked snapshot; this follows that
// pattern so the Execute path can source EPM without a backend dependency.
//
// HONEST COVERAGE — this is the point of the module, so it is stated plainly:
//   * EPM has NO plan for uc 1693 (Care Coordination & Transition Management), the demo hero.
//     Its entire Execute record is fixture-fill. The re-root moved the hero to a use case the
//     EPM pipeline has never run, and inventing an EPM lineage for it would be a fabrication.
//   * `epm_dependencies` is EMPTY (0 rows, all clients). Dependency content is fixture-fill.
//   * `epm_playbook_output` is THIN (12 rows). Playbook content is fixture-fill.
//   * `epm_milestones` has rows for 1553 / 1554 / 1555 / 1683 but NOT 1559.
//   * The populated content itself is generic — phase labels ("Phase 3: Development and
//     Customization") and a boilerplate three-risk register repeated per use case. It is
//     therefore wired ADDITIVELY: EPM fills where the fixture has nothing (pending scaffolds),
//     and never overwrites the authored demo content, which is materially richer.
//
// Provenance is carried on the record so the Command Center can say which is which.
// ============================================================

import type { ExecMilestone, ExecRisk, ExecRiskSeverity } from '../executionInitiatives';

export const EPM_PLAN_RUN_ID = 'epm-300-sep-v105';
export const EPM_PLAN_VERSION = 105;
export const EPM_PLAN_TYPE = 'sep';
export const EPM_GENERATED_AT = '2026-07-20T06:21:16Z';
export const EPM_CLIENT_ID = '300';

/** Where an Execute surface's content came from. */
export type PlanProvenance = 'epm-stage' | 'fixture-fill' | 'epm-stage+fixture-fill';

export interface PlanSource {
  provenance: PlanProvenance;
  /** Set only when EPM contributed. */
  planRunId?: string;
}
// `note` (a display string the Command Center rendered as "Plan source | ...") has been
// removed along with that line. Provenance and planRunId remain as structured metadata;
// nothing renders them today, and any future surface should format them itself rather
// than resurrect a prose field that only one component knew how to read.

// ------------------------------------------------------------
// Row counts for client 300 across every epm_* table (verified 2026-07-22). Kept here so the
// "what is populated" claim is auditable rather than asserted.
// ------------------------------------------------------------
export const EPM_TABLE_CENSUS: Record<string, number> = {
  epm_plan_header: 115,
  epm_sep_output: 55,
  epm_milestones: 1315,
  epm_schedule: 1315,
  epm_risks: 874,
  epm_governance: 165,
  epm_assumptions: 165,
  epm_context_snapshot: 115,
  epm_provenance_links: 460,
  epm_validation_result: 115,
  epm_execution_kit_output: 26,
  epm_approval_artifact_output: 20,
  epm_playbook_output: 12, // THIN
  epm_dependencies: 0, // EMPTY
};

/** Tables with no usable content for client 300 — the surfaces these feed are fixture-fill. */
export const EPM_FIXTURE_FILL_SURFACES = ['dependencies', 'playbooks'] as const;

// ------------------------------------------------------------
// epm_milestones — plan run epm-300-sep-v105
// ------------------------------------------------------------
interface EpmMilestoneRow { label: string; monthsFromStart: number; valueNote: string }

const EPM_MILESTONES: Record<number, EpmMilestoneRow[]> = {
  1553: [
    { label: 'Phase 1', monthsFromStart: 2, valueNote: 'Phase 1: Planning and Analysis (Month 1-2)' },
    { label: 'Phase 2', monthsFromStart: 4, valueNote: 'Phase 2: Design and Strategy Development (Month 3-4)' },
    { label: 'Phase 3', monthsFromStart: 7, valueNote: 'Phase 3: Technology Implementation (Month 5-7)' },
    { label: 'Phase 4', monthsFromStart: 8, valueNote: 'Phase 4: Pilot Testing (Month 8)' },
    { label: 'Phase 5', monthsFromStart: 10, valueNote: 'Phase 5: Full Implementation and Training (Month 9-10)' },
    { label: 'Phase 6', monthsFromStart: 12, valueNote: 'Phase 6: Monitoring and Continuous Improvement (Month 11-12)' },
  ],
  1554: [
    { label: 'Phase 1', monthsFromStart: 3, valueNote: 'Phase 1: Planning and Research (Month 1-3)' },
    { label: 'Phase 2', monthsFromStart: 9, valueNote: 'Phase 2: Design and Development (Month 4-9)' },
    { label: 'Phase 3', monthsFromStart: 12, valueNote: 'Phase 3: Testing and Training (Month 10-12)' },
    { label: 'Phase 4', monthsFromStart: 18, valueNote: 'Phase 4: Implementation and Evaluation (Month 13-18)' },
  ],
  1555: [
    { label: 'Phase 1', monthsFromStart: 2, valueNote: 'Phase 1: Planning and Requirements Gathering (Month 1-2)' },
    { label: 'Phase 2', monthsFromStart: 4, valueNote: 'Phase 2: Design and Vendor Selection (Month 3-4)' },
    { label: 'Phase 3', monthsFromStart: 8, valueNote: 'Phase 3: Development and Customization (Month 5-8)' },
    { label: 'Phase 4', monthsFromStart: 10, valueNote: 'Phase 4: Testing and Quality Assurance (Month 9-10)' },
    { label: 'Phase 5', monthsFromStart: 12, valueNote: 'Phase 5: Training and Deployment (Month 11-12)' },
    { label: 'Phase 6', monthsFromStart: 18, valueNote: 'Phase 6: Post-Implementation Support and Optimization (Month 13-18)' },
  ],
  1683: [
    { label: 'Executive Kickoff and Governance Setup', monthsFromStart: 1, valueNote: 'Steering committee formed and project charter signed' },
    { label: 'Current State Assessment and Gap Analysis', monthsFromStart: 2, valueNote: 'Comprehensive report on current resource utilization and gaps identified' },
    { label: 'Future-State Workflow Design', monthsFromStart: 4, valueNote: 'Blueprint of optimized workflows aligned with data-driven insights' },
    { label: 'Technology and EHR Integration', monthsFromStart: 6, valueNote: 'Integration of analytics platform with existing EHR systems completed' },
    { label: 'Pilot Testing and Validation', monthsFromStart: 8, valueNote: 'Pilot successfully tested with key metrics validated' },
    { label: 'Full Deployment and Training', monthsFromStart: 10, valueNote: 'System fully deployed with staff trained across all departments' },
    { label: 'Outcome Measurement and Optimization', monthsFromStart: 12, valueNote: 'Performance metrics analyzed and system optimizations identified' },
  ],
  // 1559 has risks but NO milestone rows in this plan run — deliberately absent, not omitted.
};

// ------------------------------------------------------------
// epm_risks — plan run epm-300-sep-v105. The register is the same boilerplate three for every
// use case except 1553, which adds a clinical data-governance risk.
// ------------------------------------------------------------
interface EpmRiskRow { name: string; category: string; likelihood: 'Low' | 'Medium' | 'High'; impact: 'Low' | 'Medium' | 'High'; owner: string; mitigation: string }

const EPM_RISK_BASE: EpmRiskRow[] = [
  {
    name: 'Assumption confidence below target',
    category: 'Financial',
    likelihood: 'Low',
    impact: 'Medium',
    owner: 'Financial Analyst',
    mitigation: 'Validate key financial assumptions against client-specific benchmarks. Review and update after Pipeline 1 profile completion.',
  },
  {
    name: 'Implementation timeline dependencies',
    category: 'Operational',
    likelihood: 'Medium',
    impact: 'Medium',
    owner: 'Programme Manager',
    mitigation: 'Plan for 6-month ramp period. Establish milestone checkpoints and vendor accountability agreements.',
  },
  {
    name: 'User adoption below target',
    category: 'Change Management',
    likelihood: 'Medium',
    impact: 'High',
    owner: 'Change Management Lead',
    mitigation: 'Target adoption rate 65% requires structured change management, training, and executive sponsorship.',
  },
];

const EPM_RISKS: Record<number, EpmRiskRow[]> = {
  1553: [
    ...EPM_RISK_BASE,
    {
      name: 'Clinical data quality and governance',
      category: 'Clinical',
      likelihood: 'Medium',
      impact: 'High',
      owner: 'Chief Medical Officer',
      mitigation: 'Establish data quality baseline before go-live. Require clinical governance review and sign-off on analytics outputs.',
    },
  ],
  1554: EPM_RISK_BASE,
  1555: EPM_RISK_BASE,
  1559: EPM_RISK_BASE,
  1683: EPM_RISK_BASE,
};

// ------------------------------------------------------------
// epm_governance / epm_assumptions — plan-level, not per use case.
// ------------------------------------------------------------
export interface EpmGovernanceStep { step: number; title: string; description: string; responsibleGroup: string }

export const EPM_GOVERNANCE_STEPS: EpmGovernanceStep[] = [
  { step: 1, title: 'Portfolio Confirmation', description: 'Confirm selected initiatives and scope.', responsibleGroup: 'Business + PMO' },
  { step: 2, title: 'Funding Gate', description: 'Approve initiative funding and staffing.', responsibleGroup: 'Finance + Exec Sponsor' },
  { step: 3, title: 'Delivery Gate', description: 'Approve production rollout readiness.', responsibleGroup: 'Engineering + Operations' },
];

/** Portfolio-level annual benefit scenarios from epm_assumptions (dollars, not millions). */
export const EPM_PORTFOLIO_SCENARIOS = {
  conservative: 55_677_050,
  base: 85_657_000,
  aggressive: 115_636_950,
} as const;

/** Use cases the plan run actually covers, in portfolio order. */
export const EPM_PORTFOLIO_UC_IDS: number[] = [1553, 1554, 1555, 1559, 1683];

// ------------------------------------------------------------
// Accessors
// ------------------------------------------------------------

export function epmCoversUseCase(useCaseId: number | undefined): boolean {
  return useCaseId !== undefined && EPM_PORTFOLIO_UC_IDS.includes(useCaseId);
}

const severityFromImpact = (impact: EpmRiskRow['impact']): ExecRiskSeverity =>
  impact === 'High' ? 'High' : impact === 'Medium' ? 'Moderate' : 'Low';

/** EPM milestones shaped for the Command Center. Empty when the plan run has none. */
export function epmMilestonesForUseCase(useCaseId: number | undefined): ExecMilestone[] {
  const rows = useCaseId === undefined ? undefined : EPM_MILESTONES[useCaseId];
  if (!rows) return [];
  return rows.map((r) => ({
    title: r.valueNote,
    phase: r.label,
    owner: 'Programme Manager',
    due: `Month ${r.monthsFromStart}`,
    status: 'Not Started' as ExecMilestone['status'],
    pct: 0,
  }));
}

/** EPM risk register shaped for the Command Center. Empty when the plan run has none. */
export function epmRisksForUseCase(useCaseId: number | undefined): ExecRisk[] {
  const rows = useCaseId === undefined ? undefined : EPM_RISKS[useCaseId];
  if (!rows) return [];
  return rows.map((r) => ({
    title: r.name,
    severity: severityFromImpact(r.impact),
    likelihood: r.likelihood,
    owner: r.owner,
    status: 'Open' as ExecRisk['status'],
    description: `${r.category} risk carried from the execution plan (${EPM_PLAN_RUN_ID}).`,
    mitigation: r.mitigation,
  }));
}

/**
 * Provenance for an initiative's Execute content.
 *
 * `authored` = the initiative has a hand-authored fixture record. Those are never overwritten,
 * so the label reflects that EPM corroborates the plan without supplying its content.
 */
export function planSourceFor(useCaseId: number | undefined, authored: boolean): PlanSource {
  const covered = epmCoversUseCase(useCaseId);
  if (covered && authored) {
    return {
      provenance: 'epm-stage+fixture-fill',
      planRunId: EPM_PLAN_RUN_ID,
      // ENGINEERING STATUS (not user-visible): EPM stage corroborates the plan but supplies no
      // milestone/workstream/team content, so that detail is fixture-fill pending a bespoke
      // Wave-2 plan run. Formerly surfaced as a "Plan source" line on the Command Center; that
      // line is gone — the demo mirrors a client, so labelling one panel as staged said nothing
      // the rest of the screen did not already imply, and invited the question it tried to answer.
    };
  }
  if (covered) {
    return {
      provenance: 'epm-stage',
      planRunId: EPM_PLAN_RUN_ID,
    };
  }
  return {
    provenance: 'fixture-fill',
    // ENGINEERING STATUS (not user-visible): no execution plan exists for this use case yet;
    // all Execute content is fixture-fill pending a Wave-2 plan run.
  };
}
